import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";

export default function Login() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Already logged in — redirect immediately
  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/portal"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      // No navigate() here — the if(user) guard above handles redirect
      // once React re-renders with the new user state
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="section-container py-16 flex items-center justify-center">
        <div className="w-full max-w-md">
          <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-black text-[#003366] mb-2">Sign In</h1>
              <p className="text-[#64748B] text-sm">Access your partner portal or admin dashboard</p>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg mb-5" data-testid="login-error">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Email Address</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="you@example.com"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-semibold text-[#0F172A]">Password</label>
                  <Link to="/forgot-password" className="text-sm text-[#003366] font-semibold hover:underline" data-testid="login-forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="input-field pr-12"
                    placeholder="Your password"
                    data-testid="login-password-input"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#003366]" data-testid="toggle-password">
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full !py-3 disabled:opacity-50"
                data-testid="login-submit-btn"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-[#64748B]">
              Don't have an account?{" "}
              <Link to="/register" className="text-[#003366] font-semibold hover:underline" data-testid="login-register-link">
                Register as a Tax Pro
              </Link>
            </div>
          </div>
          <div className="mt-6 text-center text-xs text-[#94A3B8] flex items-center justify-center gap-4">
            <Link to="/terms" className="hover:text-[#64748B] transition-colors" data-testid="login-terms-link">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="hover:text-[#64748B] transition-colors" data-testid="login-privacy-link">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
